// Section 21: the Ledger accumulator is written here, once per season, from this season's typed
// report entries (the contract is in .context/requests.md and docs/decisions.md G-01). Rationing
// writes `wentWithout`, `personSeasons` and `shortageDecisions` itself; everything else comes
// from entries: death, birth, norm, knowledge-lost, improvement, seed (eaten), festival,
// policy-changed, justice, arrival (decided), departure and refusal. Peak population and child
// labour are read from the people at the end of the season. Live mode only: projections keep no
// Ledger. Called from the steady phase after the loss check, so a Section 20 breakdown's
// departures are counted (ADR-0017; it was a decay sub-step in wave 2).
//
// This is the only writer of these fields: the M1 direct writes in killPerson and setPolicy were
// removed in M6. Rows are still de-duplicated by id and turn, births are the sum of the
// per-season birth series and the peak is a maximum, so a repeated entry never counts twice.
import { ageGroupOf, ageYears, isChild } from '../../core/queries';
import { countIds, forEachSorted } from '../../core/sorted';
import type {
  LedgerArrival,
  LedgerDeath,
  PersonId,
  PhaseContext,
  ReportEntry,
  SeasonReport,
} from '../../core/types';

type Entry<K extends ReportEntry['kind']> = Extract<ReportEntry, { kind: K }>;

function sumSeries(values: readonly number[]): number {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

function recordDeath(ctx: PhaseContext, e: Entry<'death'>, seen: Set<PersonId>): void {
  if (seen.has(e.personId)) return;
  seen.add(e.personId);
  const former = ctx.draft.formerPeople[e.personId];
  const row: LedgerDeath = {
    personId: e.personId,
    name: e.name,
    ageSeasons: e.ageSeasons,
    ageGroup: former?.ageGroup ?? ageGroupOf(ctx.cfg, e.ageSeasons),
    origin: former?.origin ?? 'founder',
    cause: e.cause,
    turn: ctx.season.turn,
  };
  ctx.draft.ledger.deaths.push(row);
}

/** Candidates of a decided arrival group; `starvingFamily` is famine with a child among them. */
function recordArrival(ctx: PhaseContext, e: Entry<'arrival'>): void {
  if (e.decision === null) return;
  const d = ctx.draft;
  const group = d.arrivalGroups[e.groupId];
  const candidateIds: PersonId[] =
    group === undefined ? e.personIds.slice() : group.candidates.map((c) => c.id);
  for (const id of e.personIds) if (!candidateIds.includes(id)) candidateIds.push(id);
  const admittedIds =
    e.decision === 'turn-away' ? [] : candidateIds.filter((id) => d.people[id] !== undefined);
  const hasChild =
    group === undefined
      ? e.personIds.some((id) => {
          const p = d.people[id];
          return p !== undefined && isChild(ctx.cfg, p);
        })
      : group.candidates.some((c) => isChild(ctx.cfg, c));
  const row: LedgerArrival = {
    turn: ctx.season.turn,
    groupId: e.groupId,
    size: candidateIds.length,
    reason: e.reason,
    decision: e.decision,
    admittedIds: admittedIds.sort(),
    starvingFamily: e.reason === 'famine' && hasChild,
  };
  const existing = d.ledger.arrivals.findIndex((a) => a.groupId === e.groupId);
  // A guest group decided again later keeps the turn it first came to the gate.
  if (existing >= 0) {
    const first = d.ledger.arrivals[existing];
    d.ledger.arrivals[existing] = { ...row, turn: first?.turn ?? row.turn };
  } else d.ledger.arrivals.push(row);
}

/** A hungry year: a food shortage in the trailing four seasons, this one included (G-06). */
function hungryYear(ctx: PhaseContext, report: SeasonReport): boolean {
  const lookback = ctx.cfg.steady.lookbackSeasons;
  const recent = [...ctx.state.history.slice(-(lookback - 1)), report];
  return recent.some((r) => r.entries.some((e) => e.kind === 'shortage' && e.resource === 'food'));
}

function childLabour(ctx: PhaseContext): boolean {
  const from = ctx.cfg.work.childWorkFromYears;
  const present = ctx.draft.people;
  let working = false;
  // Jobs and ages do not change in this phase: read the committed people (Section 2 budget),
  // skipping anyone who left in a breakdown.
  forEachSorted(ctx.state.people, (p) => {
    if (working || p.job === 'rest' || !isChild(ctx.cfg, p) || !(p.id in present)) return;
    if (ageYears(ctx.cfg, p.ageSeasons) >= from) working = true;
  });
  return working;
}

/** Called once per season from the steady phase, after the loss check (ADR-0017). */
export function accumulateLedger(ctx: PhaseContext): void {
  if (ctx.mode !== 'live') return;
  const d = ctx.draft;
  const acc = d.ledger;
  const turn = ctx.season.turn;
  const report = ctx.report.current;
  const deathsSeen = new Set<PersonId>(
    acc.deaths.filter((x) => x.turn === turn).map((x) => x.personId),
  );
  const seenThisTurn = <T extends { turn: number }>(rows: readonly T[], match: (r: T) => boolean) =>
    rows.some((r) => r.turn === turn && match(r));
  let seedEaten = 0;
  let festival = false;

  for (const e of report.entries) {
    switch (e.kind) {
      case 'death':
        recordDeath(ctx, e, deathsSeen);
        break;
      case 'norm':
        if (e.change === 'reinforced') break;
        if (seenThisTurn(acc.normEvents, (r) => r.normId === e.normId && r.change === e.change))
          break;
        acc.normEvents.push({
          turn,
          normId: e.normId,
          change: e.change,
          announced: d.norms[e.normId].announced,
        });
        break;
      case 'knowledge-lost':
        if (seenThisTurn(acc.skillsLost, (r) => r.personId === e.personId && r.skill === e.skill))
          break;
        acc.skillsLost.push({ turn, skill: e.skill, personId: e.personId, level: e.level });
        break;
      case 'improvement':
        if (seenThisTurn(acc.improvements, (r) => r.improvementId === e.improvementId)) break;
        acc.improvements.push({ turn, improvementId: e.improvementId });
        break;
      case 'seed':
        if (e.action === 'eaten') seedEaten += e.amount;
        break;
      case 'festival':
        festival = true;
        break;
      case 'policy-changed':
        if (seenThisTurn(acc.policyChanges, (r) => r.policyId === e.policyId)) break;
        acc.policyChanges.push({
          turn,
          policyId: e.policyId,
          value: JSON.stringify(d.policies[e.policyId]),
        });
        break;
      case 'justice':
        if (e.response === 'flogging') acc.corporalPunishments += 1;
        else if (e.response === 'execution') acc.executions += 1;
        else if (e.response === 'exile') acc.exiles += 1;
        break;
      case 'arrival':
        recordArrival(ctx, e);
        break;
      case 'departure':
        if (seenThisTurn(acc.departures, (r) => r.personIds[0] === e.personIds[0])) break;
        acc.departures.push({ turn, personIds: e.personIds.slice(), reason: e.reason });
        break;
      case 'refusal':
        acc.refusals += 1;
        break;
      default:
        break;
    }
  }

  if (seedEaten > 0 && !seenThisTurn(acc.seedEaten, () => true))
    acc.seedEaten.push({ turn, amount: seedEaten });
  if (festival && !seenThisTurn(acc.festivals, () => true))
    acc.festivals.push({ turn, hungryYear: hungryYear(ctx, report) });
  // Births: the run's birth series (report.population.births per season), so a direct
  // increment elsewhere cannot double-count (G-01).
  acc.births = Math.max(acc.births, sumSeries(d.series.births));
  const population = countIds(d.people);
  if (population > acc.peakPopulation.value) acc.peakPopulation = { value: population, turn };
  if (childLabour(ctx)) acc.childLabourSeasons += 1;
}
